import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { products, categories, generateGeneralWhatsAppLink } from '../data/products';
import { MessageSquare, Sparkles } from 'lucide-react';
import { PRODUCT_IMAGES, COLLECTION_PHOTOS } from '../data/images';

const Products = () => {
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('all');

  const collectionProducts = COLLECTION_PHOTOS.map((image, index) => {
    const category = categories[index % categories.length];
    const fits = ['Standard Fit', 'Slim Fit', 'Straight Fit', 'Relaxed Fit'];
    return {
      id: `collection-photo-${index + 1}`,
      name: `${category.label.replace('DK ', 'Dark ')} Collection Style ${String(index + 1).padStart(2, '0')}`,
      category: category.id,
      categoryLabel: category.label,
      color: category.label.replace('DK ', 'Dark '),
      fit: fits[index % fits.length],
      gender: 'Unisex',
      images: [image],
      features: ['International Denim', 'Standard Fit Range', 'Multiple Wash Options'],
      featured: false
    };
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const catQuery = params.get('category');
    if (catQuery) {
      if (catQuery === 'classic-blue') setSelectedCategory('rinsin-blue');
      else if (catQuery === 'dark-selvage') setSelectedCategory('blue-selvedge');
      else if (catQuery === 'black-denim') setSelectedCategory('dk-black');
      else if (catQuery === 'vintage-washed') setSelectedCategory('vintage-blue');
      else setSelectedCategory(catQuery);
    }
  }, [location.search]);

  const filteredCollectionProducts = selectedCategory === 'all'
    ? collectionProducts
    : collectionProducts.filter((product) => product.category === selectedCategory);
  const displayProducts = selectedCategory === 'all'
    ? [...products, ...filteredCollectionProducts]
    : filteredCollectionProducts;

  return (
    <div style={{ paddingTop: 'calc(var(--nav-height) + 0px)', paddingBottom: '100px' }}>

      {/* Full-width Hero Image Banner - American Eagle Style */}
      <section style={{ position: 'relative', width: '100%', height: 'clamp(240px, 34vw, 420px)', overflow: 'hidden', marginBottom: '40px' }}>
        <img
          src={PRODUCT_IMAGES[0]}
          alt="RAYYAN DENIM Global Collections"
          fetchpriority="high"
          loading="eager"
          decoding="async"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center center', display: 'block' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(15,23,42,0.2) 0%, rgba(15,23,42,0.8) 100%)' }} />
        <div style={{ position: 'absolute', bottom: '32px', left: 0, right: 0 }}>
          <div className="container" style={{ textAlign: 'center' }}>
            <span className="section-badge" style={{ background: 'rgba(255,255,255,0.18)', borderColor: 'rgba(255,255,255,0.4)', color: '#ffffff' }}>
              <Sparkles size={13} />
              INTERNATIONAL EXPORT STOREFRONT
            </span>
            <h1 style={{ fontSize: 'clamp(32px, 5.5vw, 64px)', fontFamily: 'var(--font-heading)', fontWeight: 900, textTransform: 'uppercase', color: '#ffffff', marginTop: '8px' }}>
              GLOBAL <span style={{ color: 'var(--accent-magenta)' }}>COLLECTIONS</span>
            </h1>
            <p style={{ fontSize: 'clamp(14px, 1.6vw, 18px)', color: 'rgba(255,255,255,0.85)', marginTop: '6px' }}>
              Explore Men's, Women's, New Arrivals, and Outerwear denim catalog for international buyers.
            </p>
          </div>
        </div>
      </section>

      <div className="container">
        
        {/* Category & Gender Filter Tabs (Van Heusen & American Eagle Style) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '32px'
          }}
        >
          {categories.map((cat) => {
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: '10px 22px',
                  borderRadius: '99px',
                  background: isActive ? 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-magenta) 100%)' : '#ffffff',
                  border: isActive ? 'none' : '1px solid rgba(15, 23, 42, 0.12)',
                  color: isActive ? '#ffffff' : 'var(--text-main)',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '12.5px',
                  fontWeight: isActive ? 900 : 700,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? '0 4px 16px var(--accent-glow)' : '0 2px 6px rgba(15, 23, 42, 0.04)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>{cat.icon}</span>
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Filter Count Status Bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 20px',
            background: '#f8fafc',
            borderRadius: '12px',
            marginBottom: '36px',
            border: '1px solid rgba(15, 23, 42, 0.06)'
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '0.04em' }}>
            SHOWING {displayProducts.length} EXPORT STYLES
          </div>

          <div style={{ fontSize: '12px', color: 'var(--text-subtle)', fontWeight: 600 }}>
            {selectedCategory === 'all' ? 'Featured Global Styles' : `Category: ${selectedCategory.toUpperCase()}`}
          </div>
        </div>

        {/* Product Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
            gap: '28px',
            marginBottom: '64px'
          }}
        >
          {displayProducts.map((product, idx) => (
            <ProductCard key={product.id} product={product} index={idx} />
          ))}
        </div>

        {/* Export Collection Inquiry Banner */}
        <div
          className="glass-panel"
          style={{
            padding: '44px 32px',
            textAlign: 'center',
            borderRadius: '20px',
            background: 'linear-gradient(135deg, #ffffff 0%, rgba(224, 0, 135, 0.08) 100%)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)'
          }}
        >
          <h3 style={{ fontSize: '26px', fontFamily: 'var(--font-heading)', color: 'var(--text-main)', marginBottom: '12px', fontWeight: 900 }}>
            LOOKING TO IMPORT BULK DENIM WORLDWIDE?
          </h3>
          <p style={{ color: 'var(--text-muted)', maxWidth: '640px', margin: '0 auto 28px', fontSize: '16px' }}>
            We supply malls, MBOs, retail chains, international importers, distributors, and apparel brands with export-ready denim collections and dependable export support.
          </p>
          <a
            href={generateGeneralWhatsAppLink("Global Export Requirement")}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
            style={{ padding: '15px 32px', fontSize: '13px' }}
          >
            <MessageSquare size={18} />
            DISCUSS EXPORT ORDER ON WHATSAPP
          </a>
        </div>

      </div>
    </div>
  );
};

export default Products;
